'use client';

import { useMemo } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import type { ValuationStatusHistory } from '@/lib/platform/types';

interface ValuationStatusTimelineProps {
  history: ValuationStatusHistory[];
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// Get color for status dot
function getStatusColor(status: string, darkMode: boolean) {
  switch (status) {
    case 'approved':
      return darkMode ? 'bg-green-500' : 'bg-green-600';
    case 'draft':
      return darkMode ? 'bg-gray-500' : 'bg-gray-400';
    case 'rejected':
      return darkMode ? 'bg-red-500' : 'bg-red-600';
    default:
      return darkMode ? 'bg-gray-500' : 'bg-gray-400';
  }
}

export function ValuationStatusTimeline({ history }: ValuationStatusTimelineProps) {
  const { darkMode } = useDarkMode();

  // Sort history by changed_at descending (newest first)
  const sortedHistory = useMemo(() => {
    return [...history].sort(
      (a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
    );
  }, [history]);

  if (sortedHistory.length === 0) {
    return (
      <div className={`py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No status history available.
      </div>
    );
  }

  return (
    <ul role="list" className="space-y-6">
      {sortedHistory.map((entry, entryIdx) => (
        <li key={entry.id} className="relative flex gap-x-4">
          {/* Vertical line connector */}
          <div
            className={classNames(
              entryIdx === sortedHistory.length - 1 ? 'h-6' : '-bottom-6',
              'absolute top-0 left-0 flex w-6 justify-center'
            )}
          >
            <div className={`w-px ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>

          {/* Status dot */}
          <div className="relative flex size-6 flex-none items-center justify-center">
            <div className={`size-2 rounded-full ${getStatusColor(entry.status, darkMode)}`} />
          </div>

          {/* Content */}
          <div className="flex-auto">
            <div className="flex items-start justify-between gap-x-4">
              <div className={`py-0.5 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  {((entry.metadata as Record<string, unknown>)?.user_name as string) ||
                    'Unknown user'}
                </span>{' '}
                {(entry.metadata as Record<string, unknown>)?.initial_creation ? (
                  <>
                    created as <span className="font-medium">{entry.status}</span>
                  </>
                ) : (entry.metadata as Record<string, unknown>)?.previous_status ? (
                  <>
                    changed status from{' '}
                    <span className="font-medium">
                      {(entry.metadata as Record<string, unknown>).previous_status as string}
                    </span>{' '}
                    to <span className="font-medium">{entry.status}</span>
                  </>
                ) : (
                  <>
                    set status to <span className="font-medium">{entry.status}</span>
                  </>
                )}
              </div>
              <time
                dateTime={entry.changed_at}
                className={`flex-none py-0.5 text-xs whitespace-nowrap ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}
              >
                {new Date(entry.changed_at).toLocaleString('da-DK', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </time>
            </div>

            {/* Optional reason */}
            {entry.reason && (
              <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                {entry.reason}
              </p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
